
import React, { useState, useEffect, useRef } from 'react';
import QrScanner from 'react-qr-scanner';
import { X, AlertCircle, Camera, Upload, Image } from 'lucide-react';
import { BrowserQRCodeReader } from '@zxing/library';
import './QRScannerModal.css';

const QRScannerModal = ({ open, onClose, onScan }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [showManualClose, setShowManualClose] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const scannerRef = useRef(null);
  const timeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIsScanning(true);
      setError(null);
    } else {
      setIsScanning(false);
      if (scannerRef.current && scannerRef.current.stop) {
        scannerRef.current.stop();
      }
    }
  }, [open]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
if (scannerRef.current && scannerRef.current.stop) {
        scannerRef.current.stop();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleScan = (data) => {
    if (data) {
      try {
        const scannedValue = typeof data === 'string' ? data : data.text || data;
        if(scannedValue){
          onScan(scannedValue);
          onClose();
        }
      } catch (err) {
        console.error('Error processing scanned data:', err);
        setError('Error processing scanned data. Please try again.');
      }
    }
  };

  const handleError = (err) => {
    console.error('QR Scanner Error:', err);
if (err.name === 'NotAllowedError') {
      setError('Camera access has been denied. Please enable camera permissions in your browser settings.');
    } else {
      setError('QR scanner failed to initialize. Please ensure your camera is working and not in use by another application.');
    }
    setIsScanning(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessingImage(true);
    setError(null);

    try {
      const codeReader = new BrowserQRCodeReader();
      const result = await codeReader.decodeFromImageUrl(URL.createObjectURL(file));
      
      if (result && result.getText()) {
        onScan(result.getText());
        onClose();
      } else {
        setError('No QR code found in the uploaded image. Please try another image.');
      }
    } catch (err) {
      console.error('Error processing uploaded image:', err);
      setError('Failed to process the uploaded image. Please ensure it contains a valid QR code.');
    } finally {
      setIsProcessingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Scan QR Code</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="close-button">
            <X size={24} />
          </button>
        </div>
        
<div className="relative w-full aspect-square flex items-center justify-center bg-gray-100 rounded-lg mb-4">
          {error ? (
<div className="text-red-500 text-center p-4 flex flex-col items-center">
<AlertCircle className="w-12 h-12 mb-4" />
              <p className="font-semibold">{error}</p>
              <button 
onClick={onClose}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
Close
              </button>
            </div>
          ) : isScanning ? (
<>
            <QrScanner
              ref={scannerRef}
              delay={300}
              onError={handleError}
              onScan={handleScan}
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
              constraints={{
                video: {
                  facingMode: 'environment',
                },
              }}
            />
            <div className="scanner-overlay">
              <div className={`scanner-box ${scanSuccess ? 'scan-success' : ''}`}>
                <div className="scanner-line"></div>
                <div className="scanner-grid"></div>
                <div className="scanner-corners">
                  <div className="corner top-left"></div>
                  <div className="corner top-right"></div>
                  <div className="corner bottom-left"></div>
                  <div className="corner bottom-right"></div>
                </div>
                {scanSuccess && (
                  <div className="scan-success-indicator">
                    <div className="checkmark">✓</div>
                  </div>
                )}
              </div>
            </div>
          </>
          ) : (
<div className="text-gray-500 text-center p-4 flex flex-col items-center">
              <Camera className="w-12 h-12 mb-4" />
              <p>Initializing Camera...</p>
              {showManualClose && (
                <button 
                  onClick={onClose}
                  className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
        
        <div className="text-sm text-gray-600 text-center mb-4">
          <p>Position the QR code within the camera view to scan or upload an image</p>
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
          data-testid="file-input"
        />
        
        {/* Upload Image Button */}
        <div className="flex justify-center gap-4 mb-4">
          <button
            onClick={handleUploadClick}
            disabled={isProcessingImage}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="upload-button"
          >
            {isProcessingImage ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload Image
              </>
            )}
          </button>
        </div>
        
        {/* Manual Close Button */}
        <div className="flex justify-center">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
            data-testid="manual-close-button"
          >
            Close Scanner
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;

